import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { RealtimeClient } from './realtime';
import { StorageService, ToastService, UserData } from 'fi-ui-common';
import { ControlItem } from '../interfaces/interface';
import { TimeZone } from '../pages/admin/timezones/timezone-interface';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

export interface User extends UserData {
  userTeam: {
    id: number;
    name: string;
    groupId: number;
    groupName: string;
    subsidiaryId: number;
    subsidiaryName: string;
  },
  userGroup: ControlItem;
  timeZone: TimeZone;
  isOriginator: boolean;
  isSyndicate: boolean;
  isAdmin: boolean;
  isReadOnly: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private http = inject(HttpClient);
  private realtime = inject(RealtimeClient);
  private toast = inject(ToastService);
  private router = inject(Router);
  private storage = inject(StorageService);

  user = new BehaviorSubject<User>({
    accountName: '',
    name: '',
    email: '',
    isOriginator: false,
    isSyndicate: false,
    isAdmin: false,
    isReadOnly: false,
    permissions: [],
    userTeam: null,
    timeZone: null,
    userGroup: null
  });

  login(url: string): Observable<boolean> {
    return this.http.get('security/user/details').pipe(
      tap((user: any) => this.setUserGroup(user)),
      tap((user: any) => this.setPermissions(user)),
      tap((user: any) => this.user.next(user)),
      switchMap(() => this.realtime.start()),
      map(() => true),
      tap(() => {
        const requestedUrl = this.storage.getItem('restoreUrlAfterSSO');
        if (requestedUrl) {
          this.router.navigateByUrl(requestedUrl);
          this.storage.removeItem('restoreUrlAfterSSO');
        }
      }),
      catchError(() => {
        if (url) {
          this.storage.setItem('restoreUrlAfterSSO', url);
        }
        window.location.href = environment.apiServer + 'oauth2/authorization/GlobalSyndicatePricer';
        return of(false);
      }),
    );
  }

  setUserGroup(user): void {
    user.userGroup = {
      id: user.userTeam?.groupId,
      label: user.userTeam?.groupName
    }
  }

  setPermissions(user): void {
    user.isOriginator = user.permissions.includes('ROLE_ORIGINATOR');
    user.isSyndicate = user.permissions.includes('ROLE_SYNDICATE');
    user.isAdmin = user.permissions.includes('ROLE_ADMIN');
    user.isReadOnly = user.permissions.includes('ROLE_READ_ONLY');
  }

  updateUserTimezone(timeZone: TimeZone): void {
    this.http.put('service/user', { timeZone }).subscribe(() => {
      const currentUser = this.user.value;
      this.user.next({ ...currentUser, timeZone });
      this.toast.onSuccess('Timezone updated successfully');
    })
  }
}
